/**
 * Labstep
 *
 * @module components/Filter/Menu
 * @desc Filters menu
 */

import DeviceFilterSelectContainer from 'labstep-web/components/Device/Filter/Select';
import ExperimentWorkflowFilterProtocol from 'labstep-web/components/ExperimentWorkflow/Filter/Protocol/Select';
import { ExperimentWorkflowFilterResourceSelectContainer as ExperimentWorkflowFilterResource } from 'labstep-web/components/ExperimentWorkflow/Filter/Resource/Select';
import { ExperimentWorkflowFilterSignedSelect as ExperimentWorkflowFilterSigned } from 'labstep-web/components/ExperimentWorkflow/Filter/Signed/Select';
import ExperimentWorkflowFilterStatus from 'labstep-web/components/ExperimentWorkflow/Filter/Status/Select';
import ExperimentWorkflowFilterStatusType from 'labstep-web/components/ExperimentWorkflow/Filter/StatusType/Select';
import { FileFilterExtensionSelect } from 'labstep-web/components/File/Filter/Extension/Select';
import { FilterAssignedTo } from 'labstep-web/components/Filter/AssignedTo';
import { FilterAvailableResourceItemCount } from 'labstep-web/components/Filter/AvailableResourceItemCount';
import { FilterCreatedBy } from 'labstep-web/components/Filter/CreatedBy';
import { FilterDateRange } from 'labstep-web/components/Filter/DateRange';
import { FilterDeleted } from 'labstep-web/components/Filter/Deleted';
import FilterLogCreatedBy from 'labstep-web/components/Filter/LogCreatedBy';
import { FilterResourceItemStatus } from 'labstep-web/components/Filter/ResourceItemStatus';
import { FilterTagSelectReadContainer as FilterTagSelect } from 'labstep-web/components/Filter/Tag/Select';
import { FilterPostMenu } from 'labstep-web/components/FilterPost/Menu';
import { LogFilterGroupSelectContainer as LogFilterGroupSelect } from 'labstep-web/components/Log/Filter/Group/Select';
import { LogFilterObjectClass } from 'labstep-web/components/Log/Filter/ObjectClass';
import { MoleculeFilter } from 'labstep-web/components/Molecule/Filter';
import ProtocolCollectionFilterStatus from 'labstep-web/components/ProtocolCollection/Filter/Status/Select';
import { PurchaseOrderFilterStatusSelect as PurchaseOrderFilterStatus } from 'labstep-web/components/PurchaseOrder/Filter/Status/Select';
import ResourceItemFilterResourceLocation from 'labstep-web/components/ResourceItem/Filter/ResourceLocation';
import { Filter } from 'labstep-web/constants/filters';
import { ICONS } from 'labstep-web/constants/icons';
import { useEntitySearchContext } from 'labstep-web/contexts/entity-search/hook';
import { useParamsContext } from 'labstep-web/contexts/params/hook';
import { Dropdown } from 'labstep-web/core/Dropdown';
import { useHasFeatureFlagEnabled } from 'labstep-web/hooks/feature-flag';
import type React from 'react';
import { FilterMenuList } from './List';
import type {
  FilterMenuContentProps,
  FilterMenuProps,
} from './types';

export const FilterMenuContent: React.FC<FilterMenuContentProps> = ({
  setParams,
  filters,
  searchParams,
  entityName,
}) => {
  const hasSignatureFeatureEnabled = useHasFeatureFlagEnabled(
    'signature_workflow',
  );

  const filterMenuContent = [
    {
      name: Filter.deleted,
      component: (
        <FilterDeleted
          setParams={setParams}
          searchParams={searchParams}
        />
      ),
    },
    {
      name: Filter.is_user_deleted,
      component: (
        <FilterDeleted
          setParams={setParams}
          searchParams={searchParams}
          paramKey="is_user_deleted"
        />
      ),
    },
    {
      name: Filter.is_group_deleted,
      component: (
        <FilterDeleted
          setParams={setParams}
          searchParams={searchParams}
          paramKey="is_group_deleted"
        />
      ),
    },
    {
      name: Filter.date_range,
      component: (
        <FilterDateRange
          setParams={setParams}
          searchParams={searchParams}
        />
      ),
    },
    {
      name: Filter.resource_item_status,
      component: (
        <FilterResourceItemStatus
          isOrderRequest={filters.includes(
            Filter.order_request_status,
          )}
          setParams={setParams}
          searchParams={searchParams}
        />
      ),
    },
    {
      name: Filter.order_request_status,
      component: (
        <FilterResourceItemStatus
          isOrderRequest={filters.includes(
            Filter.order_request_status,
          )}
          setParams={setParams}
          searchParams={searchParams}
        />
      ),
    },
    {
      name: Filter.available_resource_item_count,
      component: (
        <FilterAvailableResourceItemCount
          setParams={setParams}
          searchParams={searchParams}
        />
      ),
    },
    {
      name: Filter.extension,
      component: (
        <FileFilterExtensionSelect
          setParams={setParams}
          searchParams={searchParams}
        />
      ),
    },
    {
      name: Filter.created_by,
      component: (
        <FilterCreatedBy
          searchParams={searchParams}
          setParams={setParams}
          filter={Filter.created_by}
        />
      ),
    },
    {
      name: Filter.log_created_by,
      component: <FilterLogCreatedBy setParams={setParams} />,
    },
    {
      name: Filter.requested_by,
      component: (
        <FilterCreatedBy
          searchParams={searchParams}
          setParams={setParams}
          filter={Filter.requested_by}
        />
      ),
    },
    {
      name: Filter.assigned_to,
      component: (
        <FilterAssignedTo
          searchParams={searchParams}
          setParams={setParams}
        />
      ),
    },
    {
      name: Filter.tag,
      component: (
        <FilterTagSelect
          setParams={setParams}
          searchParams={searchParams}
          type={entityName}
        />
      ),
    },
    {
      name: Filter.protocol,
      component: (
        <ExperimentWorkflowFilterProtocol
          setParams={setParams}
          searchParams={searchParams}
        />
      ),
    },
    {
      name: Filter.device,
      component: (
        <DeviceFilterSelectContainer
          setParams={setParams}
          searchParams={searchParams}
        />
      ),
    },
    {
      name: Filter.resource,
      component: (
        <ExperimentWorkflowFilterResource
          setParams={setParams}
          searchParams={searchParams}
        />
      ),
      actionComponentProps: {
        text: 'Resource used',
        icon: ICONS.resource.primary,
      },
    },
    {
      name: Filter.signed,
      component: (
        <ExperimentWorkflowFilterSigned
          setParams={setParams}
          searchParams={searchParams}
        />
      ),
    },
    {
      name: Filter.experiment_workflow_status,
      component: hasSignatureFeatureEnabled ? (
        <ExperimentWorkflowFilterStatus
          setParams={setParams}
          searchParams={searchParams}
        />
      ) : null,
    },
    {
      name: Filter.experiment_workflow_status_type,
      component: (
        <ExperimentWorkflowFilterStatusType
          setParams={setParams}
          searchParams={searchParams}
        />
      ),
    },
    {
      name: Filter.protocol_collection_status,
      component: (
        <ProtocolCollectionFilterStatus
          setParams={setParams}
          searchParams={searchParams}
        />
      ),
    },
    {
      name: Filter.purchase_order_status,
      component: (
        <PurchaseOrderFilterStatus
          setParams={setParams}
          searchParams={searchParams}
        />
      ),
    },
    {
      name: Filter.resource_location,
      component: (
        <ResourceItemFilterResourceLocation
          setParams={setParams}
          searchParams={searchParams}
        />
      ),
    },
    {
      name: Filter.molecule,
      component: (
        <MoleculeFilter
          entityName={entityName}
          setParams={setParams}
          searchParams={searchParams}
        />
      ),
      actionComponentProps: {
        text: 'Chemical Structure',
        icon: ICONS.molecule.primary,
      },
    },
    {
      name: Filter.group,
      component: (
        <LogFilterGroupSelect
          setParams={setParams}
          searchParams={searchParams}
        />
      ),
      actionComponentProps: {
        text: 'Workspace',
        icon: ICONS.group.primary,
      },
    },
    {
      name: Filter.log_object_class,
      component: (
        <LogFilterObjectClass
          setParams={setParams}
          searchParams={searchParams}
        />
      ),
      actionComponentProps: {
        text: 'Entity Type',
        icon: ICONS.entity.primary,
      },
    },
  ];

  return (
    <FilterMenuList
      filtersContent={filterMenuContent}
      filters={filters}
    />
  );
};

export const FilterMenu: React.FC<FilterMenuProps> = ({
  trigger,
  ...rest
}) => {
  const { searchParams, setParams, addPostFilter } =
    useParamsContext();
  const { usePostFilter } = useEntitySearchContext();
  return (
    <Dropdown
      data-testid="filter-menu"
      data-cy="components/Filter/Menu"
      icon={null}
      trigger={trigger}
      pointing="top right"
    >
      {usePostFilter ? (
        <FilterPostMenu
          searchParams={searchParams}
          setParams={setParams}
          addPostFilter={addPostFilter}
          {...rest}
        />
      ) : (
        <FilterMenuContent
          searchParams={searchParams}
          setParams={setParams}
          addPostFilter={addPostFilter}
          {...rest}
        />
      )}
    </Dropdown>
  );
};
